import { useAtomValue } from 'jotai';
import { buildPolygonStyle } from 'lib/map';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from './ordered-layer';
import { createMapSourceAtom } from './atoms';
import { MapUserConfigAtom } from './types';
import { mapColors } from './map-colors';
import { useMemo } from 'react';

const STYLE = buildPolygonStyle({
  color: mapColors.typeCurvesFill,
  fillOpacity: 0.05,
  lineOpacity: 0.8,
});
interface Props {
  style?: typeof STYLE;
  mapConfigAtom: MapUserConfigAtom;
  /**
   * Extra gate on top of the user's own `typeCurves` map config.
   *
   * A host that offers its own control over type-curve area geometry passes
   * false to suppress this layer, WITHOUT writing `mapConfig` -- that config is
   * persisted and shared with every other map, so a scoped toggle must not
   * change what other pages show.
   */
  visible?: boolean;
}

const TypeCurvesLayer = ({
  style = STYLE,
  mapConfigAtom,
  visible = true,
}: Props) => {
  const typeCurvesSourceAtom = useMemo(
    () => createMapSourceAtom('typeCurves'),
    []
  );
  const typeCurvesSource = useAtomValue(typeCurvesSourceAtom);
  const mapConfig = useAtomValue(mapConfigAtom);
  if (!typeCurvesSource) {
    return null;
  }

  return (
    <LayerBand band={LAYER_ORDER.typeCurves}>
      <Source id="type-curves" type="vector" url={typeCurvesSource}>
        {mapConfig.typeCurves?.checked && visible && (
          <Layer
            id="type-curves-line"
            type="line"
            paint={style.line}
            filter={['==', ['get', 'type'], 'shape']}
            source-layer="type-curves"
          />
        )}
        {mapConfig.typeCurves?.checked && visible && (
          <Layer
            id="type-curves-label"
            type="symbol"
            source-layer="type-curves"
            filter={['==', 'type', 'centroid']}
            layout={{
              'text-field': ['get', 'name'],
            }}
            paint={{
              'text-color': STYLE.fill['fill-color'],
            }}
          />
        )}
      </Source>
    </LayerBand>
  );
};

export { TypeCurvesLayer };
